import i18n from '../i18n';
import { monthLong, monthShort } from './intlFormatters';

// Date range modes
export const DATE_MODES = {
  ALL: 'all',
  MONTH: 'month',
  WEEK: 'week',
  YEAR: 'year',
  CUSTOM: 'custom'
};

// Get "All" date range (no date filter)
export const getAllDateRange = () => {
  return {
    mode: DATE_MODES.ALL,
    start_date: null,
    end_date: null,
    reference: null
  };
};

// Format a date to YYYY-MM-DD string
export const formatDateString = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse YYYY-MM-DD string to Date object
export const parseDateString = (dateStr) => {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-based
  return { year, month };
};

// Get current date range based on mode (defaults to current month)
export const getCurrentDateRange = (mode = DATE_MODES.MONTH) => {
  const now = new Date();
  
  switch (mode) {
    case DATE_MODES.WEEK:
      return getWeekDateRange(now);
    case DATE_MODES.YEAR:
      return getYearDateRange(now.getFullYear());
    case DATE_MODES.MONTH:
    default:
      return getMonthDateRange(now.getFullYear(), now.getMonth());
  }
};

export const getMonthDateRange = (year, month) => {
  const startDate = new Date(year, month, 1);
  const endDate = new Date(year, month + 1, 0); // Last day of month
  
  return {
    mode: DATE_MODES.MONTH,
    start_date: formatDateString(startDate),
    end_date: formatDateString(endDate),
    reference: { year, month }
  };
};

// Get week date range (Monday to Sunday)
export const getWeekDateRange = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust for Monday start (0 = Sunday, so Monday = 1)
  const diff = day === 0 ? -6 : 1 - day;
  
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  
  return {
    mode: DATE_MODES.WEEK,
    start_date: formatDateString(monday),
    end_date: formatDateString(sunday),
    reference: { 
      year: monday.getFullYear(), 
      month: monday.getMonth(),
      weekStart: formatDateString(monday)
    }
  };
};

// Get year date range
export const getYearDateRange = (year) => {
  const startDate = new Date(year, 0, 1);
  const endDate = new Date(year, 11, 31);
  
  return {
    mode: DATE_MODES.YEAR,
    start_date: formatDateString(startDate),
    end_date: formatDateString(endDate),
    reference: { year }
  };
};

// Check if a date string (YYYY-MM-DD) falls within a date range
export const isDateWithinRange = (dateStr, dateRange) => {
  if (!dateRange || dateRange.mode === DATE_MODES.ALL) return true;
  if (!dateStr || !dateRange.start_date || !dateRange.end_date) return true;
  return dateStr >= dateRange.start_date && dateStr <= dateRange.end_date;
};

// Get the date range that contains a given date, preserving the specified mode
export const getDateRangeForDate = (dateStr, currentMode) => {
  const d = parseDateString(dateStr);
  switch (currentMode) {
    case DATE_MODES.ALL:
      return getAllDateRange();
    case DATE_MODES.YEAR:
      return getYearDateRange(d.getFullYear());
    case DATE_MODES.WEEK:
      return getWeekDateRange(d);
    case DATE_MODES.MONTH:
    case DATE_MODES.CUSTOM:
    default:
      return getMonthDateRange(d.getFullYear(), d.getMonth());
  }
};

// Get custom date range
export const getCustomDateRange = (startDate, endDate) => {
  return {
    mode: DATE_MODES.CUSTOM,
    start_date: formatDateString(startDate),
    end_date: formatDateString(endDate),
    reference: null
  };
};

// Navigate to previous/next period based on mode
export const navigateDateRange = (currentRange, direction) => {
  const delta = direction === 'prev' ? -1 : 1;
  
  switch (currentRange.mode) {
    case DATE_MODES.MONTH: {
      const ref = currentRange.reference;
      let newMonth = ref.month + delta;
      let newYear = ref.year;
      
      if (newMonth < 0) {
        newMonth = 11;
        newYear -= 1;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear += 1;
      }
      
      return getMonthDateRange(newYear, newMonth);
    }
    
    case DATE_MODES.WEEK: {
      const currentStart = parseDateString(currentRange.start_date);
      const newStart = new Date(currentStart);
      newStart.setDate(currentStart.getDate() + (delta * 7));
      return getWeekDateRange(newStart);
    }
    
    case DATE_MODES.YEAR: {
      const newYear = currentRange.reference.year + delta;
      return getYearDateRange(newYear);
    }
    
    case DATE_MODES.CUSTOM:
    default:
      // Custom mode doesn't support navigation, return as-is
      return currentRange;
  }
};

// Jump to today's period based on mode
export const getTodayDateRange = (mode) => {
  return getCurrentDateRange(mode);
};

// Change mode while preserving approximate date context
export const changeDateRangeMode = (currentRange, newMode) => {
  // Handle ALL mode
  if (newMode === DATE_MODES.ALL) {
    return getAllDateRange();
  }
  
  // Use the start date of current range as reference point (or today if null)
  const refDate = currentRange?.start_date 
    ? parseDateString(currentRange.start_date) 
    : new Date();
  
  switch (newMode) {
    case DATE_MODES.MONTH:
      return getMonthDateRange(refDate.getFullYear(), refDate.getMonth());
    case DATE_MODES.WEEK:
      return getWeekDateRange(refDate);
    case DATE_MODES.YEAR:
      return getYearDateRange(refDate.getFullYear());
    case DATE_MODES.CUSTOM:
      // Keep current range but mark as custom
      return {
        ...currentRange,
        mode: DATE_MODES.CUSTOM,
        reference: null
      };
    default:
      return currentRange;
  }
};

export const formatMonthYear = (year, month) => {
  return `${monthLong(month)} ${year}`;
};

/** A month as an address writes it: (2026, 8) → `2026-08`. `month` is 1-12. */
export const toMonthParam = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

/** `2026-08` → `{ year: 2026, month: 8 }`, or null for anything else. */
export const fromMonthParam = (value) => {
  const match = /^(\d{4})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? { year: Number(match[1]), month } : null;
};

// Format date range for display based on mode
export const formatDateRangeDisplay = (dateRange) => {
  if (!dateRange) {
    return '';
  }
  const { mode, start_date, end_date, reference } = dateRange;
  
  // Handle ALL mode
  if (mode === DATE_MODES.ALL) {
    return i18n.t('all', { ns: 'dates' });
  }

  switch (mode) {
    case DATE_MODES.MONTH: {
      const { year, month } = reference;
      return `${monthLong(month)} ${year}`;
    }

    case DATE_MODES.WEEK: {
      const start = parseDateString(start_date);
      const end = parseDateString(end_date);
      const startMonth = monthShort(start.getMonth());
      const endMonth = monthShort(end.getMonth());

      if (start.getMonth() === end.getMonth()) {
        return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${end.getFullYear()}`;
      } else if (start.getFullYear() === end.getFullYear()) {
        return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
      } else {
        return `${startMonth} ${start.getDate()}, ${start.getFullYear()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
      }
    }

    case DATE_MODES.YEAR: {
      return `${reference.year}`;
    }

    case DATE_MODES.CUSTOM: {
      const start = parseDateString(start_date);
      const end = parseDateString(end_date);
      const startMonth = monthShort(start.getMonth());
      const endMonth = monthShort(end.getMonth());

      if (start.getFullYear() === end.getFullYear()) {
        return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
      } else {
        return `${startMonth} ${start.getDate()}, ${start.getFullYear()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
      }
    }

    default:
      return `${start_date} - ${end_date}`;
  }
};
